import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Card, Alert, Spinner, InfoRow, Input, Textarea, TagsInput, ScopeBadges, Select } from './shared';

const formatTokens = (count) => {
    if (!count) return null;
    if (count >= 1000) return (count / 1000).toFixed(1) + 'k';
    return String(count);
};

export default function AdminKnowledgeForm() {
    const { knowledgeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = location.pathname.endsWith('/edit');
    const isCreate = !knowledgeId;
    const isView = !!knowledgeId && !isEdit;

    const [data, setData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [error, setError] = useState(null);
    const [environments, setEnvironments] = useState([]);

    useEffect(() => { loadData(); adminApi.listEnvironments().then(setEnvironments).catch(() => {}); }, [knowledgeId]);

    const loadData = async () => {
        setLoading(true);
        try {
            if (isCreate) {
                const initial = {
                    name: '',
                    description: '',
                    content: '',
                    owner_type: 'system',
                    tags: [],
                };
                setData(initial);
                setFormData(initial);
            } else {
                const kf = await adminApi.getKnowledge(knowledgeId);
                setData(kf);
                setFormData({ ...kf });
            }
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load knowledge file');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            if (isCreate) {
                await adminApi.createKnowledge(formData);
            } else {
                await adminApi.updateKnowledge(knowledgeId, formData);
            }
            navigate('/admin/knowledge');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async () => {
        const newStatus = data.status === 'active' ? 'inactive' : 'active';
        setToggling(true);
        setError(null);
        try {
            await adminApi.updateKnowledge(knowledgeId, { status: newStatus });
            setData(prev => ({ ...prev, status: newStatus }));
        } catch (err) {
            setError(err.message);
        } finally {
            setToggling(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading..." center />;
    if (!data && !isCreate) return <Alert variant="error">Knowledge file not found</Alert>;

    const title = isCreate ? 'Create Knowledge' : isView ? data.name : `Edit: ${data.name}`;

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/knowledge" className="text-gray-400 hover:text-gray-600 shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                    </svg>
                </Link>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-semibold text-gray-900 truncate">{title}</h1>
                        {isView && data.is_builtin && <Badge variant="success">Built-in</Badge>}
                        {isView && data.name && <Badge variant="outline">{data.name}</Badge>}
                        {isView && formatTokens(data.token_count) && (
                            <Badge variant="default">{formatTokens(data.token_count)} tokens</Badge>
                        )}
                    </div>
                    {knowledgeId && <p className="text-sm text-gray-500 mt-0.5">{knowledgeId}</p>}
                </div>
                {isView && (
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost"
                            className={data.status === 'active' ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' : 'text-green-600 hover:text-green-700 hover:bg-green-50'}
                            onClick={handleToggleStatus} loading={toggling}>
                            {data.status === 'active' ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button size="sm" onClick={() => navigate(`/admin/knowledge/${knowledgeId}/edit`)}>Edit</Button>
                    </div>
                )}
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            {isView ? (
                <div className="space-y-4">
                    <Card title="General">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                            <InfoRow label="ID" value={data.id} mono />
                            <InfoRow label="Name" value={<Badge variant="outline">{data.name}</Badge>} />
                            <InfoRow label="Description" value={data.description || '-'} />
                            <InfoRow label="Owner Type" value={data.owner_type} />
                            <InfoRow label="Environment" value={<Badge variant="outline">{data.environment || 'default'}</Badge>} />
                            <InfoRow
                                label="Tags"
                                value={
                                    (data.tags || []).length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {(data.tags || []).map((t) => (
                                                <Badge key={t} variant="info">#{t}</Badge>
                                            ))}
                                        </div>
                                    ) : <span className="text-gray-400">-</span>
                                }
                            />
                            <InfoRow label="Built-in" value={data.is_builtin ? 'Yes' : 'No'} />
                            <InfoRow label="Status" value={
                                <Badge variant={data.status === 'active' ? 'success' : 'warning'} dot>
                                    {data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}
                                </Badge>
                            } />
                            {data.s3_url && (
                                <InfoRow label="S3" value={
                                    <a href={data.s3_url} target="_blank" rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs break-all">
                                        {data.s3_key}
                                    </a>
                                } />
                            )}
                        </div>
                    </Card>
                    {data.content && (
                        <Card title="Content">
                            <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[70vh]">
                                {data.content}
                            </pre>
                        </Card>
                    )}
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Card title="General">
                        <Input
                            label="Name" hint="URL-safe slug used in /knowledge/{name}/KNOWLEDGE.md — allowed: A-Z a-z 0-9 _ -"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
                            placeholder="nebula_faq"
                            pattern="^[A-Za-z0-9_-]+$"
                            required
                        />
                        <Input label="Description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Brief description of this knowledge file" />
                        <TagsInput
                            label="Tags"
                            value={formData.tags || []}
                            onChange={(tags) => handleChange('tags', tags)}
                            helpText="Agents opt-in via config.knowledge.include_tags = [...]."
                        />
                        <Select
                            label="Environment"
                            value={formData.environment || ''}
                            options={[{ value: '', label: '— default env —' }, ...environments.map(e => ({ value: e.id, label: `${e.id} ${e.is_default ? '(default)' : ''}` }))]}
                            onChange={(e) => handleChange('environment', e.target.value)}
                        />
                    </Card>
                    <Card title="Content">
                        <Textarea label="Knowledge Content" monospace rows={18} value={formData.content} onChange={(e) => handleChange('content', e.target.value)} placeholder="Enter knowledge content in markdown format..." required />
                    </Card>
                    <div className="flex items-center justify-end gap-2 pt-2 pb-4">
                        <Button variant="ghost" type="button" onClick={() => navigate('/admin/knowledge')}>Cancel</Button>
                        <Button type="submit" loading={saving}>{isCreate ? 'Create Knowledge' : 'Save Changes'}</Button>
                    </div>
                </form>
            )}
        </div>
    );
}
